import { RefObject, useLayoutEffect, useRef } from 'react';
import { ProgrammaticScrollGuard } from './registry/use-programmatic-scroll-guard';

interface UseComposerDockHeightParams {
  liveDockHeight: number;
  isDragging: boolean;
  chatRef: RefObject<HTMLDivElement | null>;
  scrollGuard: ProgrammaticScrollGuard;
}

interface UseComposerDockHeightResult {
  committedDockHeight: number;
}

// The thread reserves the dock's measured height as paddingBottom, and it's a column-reverse
// scroller: changing that padding shifts scrollTop and can fire the pagination/new-messages
// handlers even though the user never touched the thread. Freezing the committed height while
// dragging keeps the thread from ever seeing an intermediate value.
export function useComposerDockHeight({
  liveDockHeight,
  isDragging,
  chatRef,
  scrollGuard,
}: UseComposerDockHeightParams): UseComposerDockHeightResult {
  const committedDockHeightRef = useRef(liveDockHeight);
  if (!isDragging) {
    committedDockHeightRef.current = liveDockHeight;
  }
  const committedDockHeight = committedDockHeightRef.current;

  // Compensates the scroller for every change to the reserved padding (drag release, banners
  // appearing, session-restored height) so the messages on screen stay pixel-stable.
  const prevCommittedDockHeightRef = useRef<number | null>(null);
  useLayoutEffect(() => {
    const prev = prevCommittedDockHeightRef.current;
    prevCommittedDockHeightRef.current = committedDockHeight;

    if (prev === null) return;

    const delta = committedDockHeight - prev;
    if (delta === 0) return;

    const el = chatRef.current;
    if (!el) return;

    // Pinned at the newest message (scrollTop ~ 0 here) must stay pinned — compensating
    // would visibly detach the thread from the bottom.
    const MAX_PINNED_SCROLL_TOP_PX = 2;
    if (Math.abs(el.scrollTop) <= MAX_PINNED_SCROLL_TOP_PX) return;

    // Measured: in this scroller, growing the reserved padding by `delta` shifts content up by
    // the same amount as increasing scrollTop by `delta`, so subtracting it cancels the shift
    // whichever way the dock moved.
    scrollGuard.run(() => {
      el.scrollTop -= delta;
    });
  }, [committedDockHeight, chatRef, scrollGuard]);

  return { committedDockHeight };
}
